<div class="help-browser" (keydown.escape)="onEscape($event)">
  <div class="help-toolbar">
    <button
      pButton
      type="button"
      icon="pi pi-chevron-left"
      class="p-button-outlined p-button-secondary help-back"
      [label]="'common.instructions-browser.back' | translate"
      [disabled]="!history.length"
      (click)="back()"
      data-cy="help-back"
    ></button>
    <div class="help-search">
      <input
        #searchInput
        pInputText
        type="text"
        class="help-search-input"
        [value]="query"
        [placeholder]="
          'common.instructions-browser.search-placeholder'
            | translate: { count: pageCount }
        "
        [attr.aria-label]="
          'common.instructions-browser.search-placeholder'
            | translate: { count: pageCount }
        "
        (focus)="prepareSearch()"
        (input)="onQueryInput(searchInput.value)"
        (keydown)="onSearchKeydown($event)"
        data-cy="help-search"
      />
      @if (query) {
        <button
          type="button"
          class="help-search-clear"
          [attr.aria-label]="
            'common.instructions-browser.clear-search' | translate
          "
          (click)="clearQuery()"
        >
          <i class="pi pi-times"></i>
        </button>
      }
    </div>
    @if (canExport) {
      <!-- As the visit report's print: the button prints in the default
        language, the chevron offers Finnish and English. -->
      <span class="help-export">
        <button
          pButton
          type="button"
          icon="pi pi-file-export"
          class="p-button-outlined"
          [label]="
            (exporting
              ? 'common.instructions-browser.export-pdf-busy'
              : 'common.instructions-browser.export-pdf'
            ) | translate
          "
          [attr.title]="'common.instructions-browser.export-pdf' | translate"
          [loading]="exporting"
          [disabled]="exporting"
          (click)="exportManual()"
          data-cy="help-export-pdf"
        ></button>
        <button
          pButton
          type="button"
          icon="pi pi-chevron-down"
          class="p-button-outlined help-export-language"
          [attr.title]="
            'common.instructions-browser.export-pdf-language' | translate
          "
          [attr.aria-label]="
            'common.instructions-browser.export-pdf-language' | translate
          "
          [disabled]="exporting"
          (click)="exportLanguages.toggle($event)"
          data-cy="help-export-language"
        ></button>
        <p-popover #exportLanguages>
          <div class="help-export-menu">
            <span class="help-export-menu-title">
              {{
                'common.instructions-browser.export-pdf-language' | translate
              }}
            </span>
            @for (language of exportLanguageOptions; track language.code) {
              <button
                type="button"
                class="help-export-menu-option"
                data-cy="help-export-language-option"
                [attr.data-lang]="language.code"
                [disabled]="exporting"
                (click)="exportLanguages.hide(); exportManual(language.code)"
              >
                <span>{{ language.label }}</span>
                @if (language.code === defaultExportLanguage) {
                  <span
                    class="pi pi-check help-export-menu-default"
                    data-cy="help-export-language-default"
                  ></span>
                }
              </button>
            }
          </div>
        </p-popover>
      </span>
    }
    <button
      type="button"
      class="help-menu-button"
      [class.help-menu-button-open]="menuOpen"
      [attr.aria-expanded]="menuOpen"
      (click)="toggleMenu()"
      data-cy="help-menu-button"
    >
      <i class="pi pi-bars"></i>
      <span>{{
        'common.instructions-browser.all-instructions' | translate
      }}</span>
      <i
        class="pi"
        [class.pi-chevron-up]="menuOpen"
        [class.pi-chevron-down]="!menuOpen"
      ></i>
    </button>
  </div>

  <div class="help-main">
    <div class="help-body" #body>
      @if (searching) {
        <section class="help-results" data-cy="help-results">
          @if (hits === null) {
            <p class="help-results-status">
              {{ 'common.instructions-browser.searching' | translate }}
            </p>
          } @else if (!hits.length) {
            <p class="help-results-status" data-cy="help-no-results">
              {{ 'common.instructions-browser.no-results' | translate }}
            </p>
          } @else {
            <p class="help-results-summary">
              <strong>{{
                (hits.length === 1
                  ? 'common.instructions-browser.result-count-one'
                  : 'common.instructions-browser.result-count'
                ) | translate: { count: hits.length }
              }}</strong>
              {{
                'common.instructions-browser.result-query'
                  | translate: { query: query.trim() }
              }}
            </p>
            <div class="help-filters" role="group">
              <button
                type="button"
                class="help-filter"
                [class.help-filter-active]="filter === null"
                (click)="setFilter(null)"
                data-cy="help-filter-all"
              >
                {{ 'common.instructions-browser.filter-all' | translate }}
                {{ hits.length }}
              </button>
              @for (f of sectionFilters; track f.id) {
                <button
                  type="button"
                  class="help-filter"
                  [class.help-filter-active]="filter === f.id"
                  (click)="setFilter(f.id)"
                  [attr.data-cy]="'help-filter-' + f.id"
                >
                  {{ sectionLabel(f.id) }} {{ f.count }}
                </button>
              }
            </div>
            <ul class="help-result-list">
              @for (hit of visibleHits; track hit.slug; let i = $index) {
                <li>
                  <button
                    type="button"
                    class="help-result"
                    [class.help-result-active]="i === activeResult"
                    (mouseenter)="activeResult = i"
                    (click)="open(hit.slug)"
                    data-cy="help-result"
                  >
                    <span class="help-result-path">
                      {{ sectionLabel(sectionOf(hit.slug)) }}
                      @if (parentOf(hit.slug); as parent) {
                        › {{ title(parent) }}
                      }
                    </span>
                    <span class="help-result-title">{{ title(hit.slug) }}</span>
                    <!-- Segments must touch: whitespace between them would split highlighted words. -->
                    <!-- prettier-ignore -->
                    <span class="help-result-snippet">@for (part of hit.snippet; track $index) {@if (part.hit) {<mark>{{ part.text }}</mark>} @else {{{ part.text }}}}</span>
                  </button>
                </li>
              }
            </ul>
            <div class="help-results-footer">
              @if (filteredHits.length > visibleHits.length) {
                <button
                  type="button"
                  class="help-link-button"
                  (click)="showAllResults()"
                  data-cy="help-show-more"
                >
                  {{
                    'common.instructions-browser.show-more'
                      | translate
                        : { count: filteredHits.length - visibleHits.length }
                  }}
                  ›
                </button>
              }
              <span class="help-keyboard-hint">
                {{ 'common.instructions-browser.keyboard-hint' | translate }}
              </span>
            </div>
          }
        </section>
      } @else {
        <div
          class="help-page"
          [class.help-page-with-aside]="outline.length || related.length"
        >
          <article class="help-article">
            @if (currentSection) {
              <nav
                class="help-breadcrumb"
                [attr.aria-label]="
                  'common.instructions-browser.breadcrumb' | translate
                "
                data-cy="help-breadcrumb"
              >
                <button
                  type="button"
                  class="help-crumb"
                  (click)="openMenu(currentSection)"
                >
                  {{ sectionLabel(currentSection) }}
                </button>
                <span class="help-crumb-separator">›</span>
                @if (parentOf(slug); as parent) {
                  <button
                    type="button"
                    class="help-crumb"
                    (click)="open(parent)"
                  >
                    {{ title(parent) }}
                  </button>
                  <span class="help-crumb-separator">›</span>
                }
                <span class="help-crumb-current">{{ title(slug) }}</span>
              </nav>
            }
            @if (title(slug); as pageTitle) {
              <h1 class="help-title">{{ pageTitle }}</h1>
            }
            <app-instruction-page
              [slug]="slug"
              [pageTitle]="title(slug)"
              (slugChange)="open($event)"
              (pageInfo)="onPageInfo($event)"
            ></app-instruction-page>
          </article>

          @if (outline.length || related.length) {
            <aside class="help-aside">
              @if (outline.length) {
                <h2 class="help-aside-title">
                  {{ 'common.instructions-browser.on-this-page' | translate }}
                </h2>
                <ul class="help-outline" data-cy="help-outline">
                  @for (entry of outline; track entry.index; let i = $index) {
                    <li>
                      <button
                        type="button"
                        class="help-outline-item"
                        [class.help-outline-sub]="entry.depth > 0"
                        [class.help-outline-active]="i === activeHeading"
                        (click)="goToHeading(i)"
                      >
                        {{ entry.text }}
                      </button>
                    </li>
                  }
                </ul>
              }
              @if (related.length) {
                <h2 class="help-aside-title">
                  {{ 'common.instructions-browser.related' | translate }}
                </h2>
                <ul class="help-related" data-cy="help-related">
                  @for (relatedSlug of related; track relatedSlug) {
                    <li>
                      <button
                        type="button"
                        class="help-link-button"
                        (click)="open(relatedSlug)"
                      >
                        {{ title(relatedSlug) }}
                      </button>
                    </li>
                  }
                </ul>
              }
            </aside>
          }
        </div>
      }
    </div>

    @if (menuOpen) {
      <div class="help-menu-backdrop" (click)="menuOpen = false"></div>
      <app-help-menu-panel
        class="help-menu-panel"
        [currentSlug]="slug"
        [section]="menuSection"
        (pageSelected)="open($event)"
      ></app-help-menu-panel>
    }
  </div>
</div>
